import { useTheme } from "@/src/styles/theme-provider";
import { forwardRef, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  NativeSyntheticEvent,
  Text,
  TextInput,
  TextInputProps,
  TextInputSubmitEditingEventData,
  View,
} from "react-native";

type InputFieldProps<T extends FieldValues> = Readonly<{
  label?: string;
  showLabel?: boolean;
  placeholder?: string;
  keyboardType?: "default" | "email-address" | "numeric" | "phone-pad";
  control: Control<T>;
  name: Path<T>;
  error?: string;
  success?: string;
  backgroundColor?: string;
  placeholderColor?: string;
  height?: number;
  rules?: Omit<
    RegisterOptions<T, Path<T>>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  borderRadius?: string;
  onChangeText?: (text: string) => void;
  multiline?: boolean;
  numberOfLines?: number;
  returnKeyType?: "done" | "go" | "next" | "search" | "send";
  blurOnSubmit?: boolean;
  onSubmitEditing?: (
    e: NativeSyntheticEvent<TextInputSubmitEditingEventData>,
  ) => void;
  /**
   * Names the field's role for autofill (e.g. "username", "email").
   *
   * On web react-native-web forwards this straight to the DOM `autocomplete`
   * attribute; left unset it falls back to a bare `autocomplete="on"`, which
   * only grants permission to autofill and never identifies the field — so
   * browsers cannot pair it with a password to offer saving the credential.
   */
  autoComplete?: TextInputProps["autoComplete"];
}>;

function InputFieldInner<T extends FieldValues>(
  {
    label,
    showLabel = false,
    placeholder,
    keyboardType = "default",
    control,
    name,
    rules,
    error,
    backgroundColor,
    placeholderColor,
    height,
    borderRadius = "rounded-lg",
    onChangeText,
    multiline = false,
    numberOfLines = 1,
    returnKeyType,
    blurOnSubmit,
    onSubmitEditing,
    autoComplete,
  }: InputFieldProps<T>,
  ref: React.Ref<TextInput>,
) {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [isFocused, setIsFocused] = useState(false);

  const resolvedPlaceholderColor =
    placeholderColor ?? (themeVars["--color-slate-default"] as string);

  const heightClass = height ? `h-${height}` : multiline ? "" : "h-16 web:h-10";

  return (
    <View className={`mb-2 web:mb-4 ${multiline ? "flex-none" : "flex-1"}`}>
      {showLabel && <Text className="font-medium pb-2 text-onyx">{label}</Text>}

      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInput
            ref={ref}
            value={value}
            onChangeText={
              onChangeText
                ? (text) => {
                    onChange(text);
                    onChangeText(text);
                  }
                : onChange
            }
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              onBlur();
            }}
            keyboardType={keyboardType}
            autoCapitalize="none"
            autoComplete={autoComplete}
            returnKeyType={returnKeyType}
            blurOnSubmit={blurOnSubmit}
            onSubmitEditing={onSubmitEditing}
            placeholder={placeholder}
            placeholderTextColor={resolvedPlaceholderColor}
            multiline={multiline}
            numberOfLines={multiline ? numberOfLines : 1}
            textAlignVertical={multiline ? "top" : "center"}
            className={`relative w-full ${borderRadius} px-4 py-3 ${backgroundColor ?? "bg-obsidian"} text-onyx focus:outline-none border
              ${heightClass}
              ${multiline ? "min-h-[100px]" : ""}
              ${error ? "border-crimson" : isFocused ? "border-primary" : "border-transparent md:border-ash-3"}`}
          />
        )}
      />

      {error && (
        <Text className="text-crimson text-xs mt-1 mb-2">{t(error)}</Text>
      )}
    </View>
  );
}

const InputField = forwardRef(InputFieldInner) as <T extends FieldValues>(
  props: InputFieldProps<T> & { ref?: React.Ref<TextInput> },
) => React.ReactElement;

export default InputField;
